export const BG_PRESETS = {
  nyc: '/bg_presets/bg_preset_nyc.jpg',
  valley: '/bg_presets/bg_preset_valley.jpg',
  jalapao: '/bg_presets/bg_preset_jalapao.jpg',
  fjord: '/bg_presets/bg_preset_fjord.jpg',
  rio: '/bg_presets/bg_preset_rio.jpg',
  city: '/bg_presets/bg_preset_city.jpg',
  turquoise_lake: '/bg_presets/bg_preset_turquoise_lake.jpg',
  dolomites: '/bg_presets/bg_preset_dolomites.jpg',
} as const;

export type BgPresetKey = keyof typeof BG_PRESETS;

export const BG_PRESET_KEYS = Object.keys(BG_PRESETS) as BgPresetKey[];

export const AUTO_KEY = 'default';

export const LUMINANCE_THRESHOLD = 0.55;

// Luminance values measured from each preset; keep in sync with BG_PRESETS.
// All values currently below the threshold, so every curated preset renders
// with the light theme (white fg) — avoids surprise flips as the pool grows.
export const PRESET_LUMINANCES: Record<BgPresetKey, number> = {
  fjord: 0.41,
  rio: 0.44,
  jalapao: 0.46,
  nyc: 0.47,
  city: 0.50,
  turquoise_lake: 0.50,
  dolomites: 0.51,
  valley: 0.51,
};

export type SidebarTheme = 'light' | 'dark';

export const SIDEBAR_FG_COLORS: Record<SidebarTheme, string> = {
  light: '#ffffff',
  dark: '#0f172a',
};

// Text shadow keeps the sidebar text readable against high-variance wallpapers
// (e.g. a bright sky behind white text). The shadow is the contrast complement
// of the fg, not a uniform dark drop — so it flips with the theme.
// Layered text shadow: a tight dark outline (no offset, 2px blur) lifts each
// glyph off bright regions, a short near-drop adds weight, and a wide halo
// softens the edge. Needed because wallpaper luminance is a global average
// and local hotspots (bright sky behind the sidebar) can still wash out text.
// Dark theme runs over light surfaces (white content cards or bright wallpapers)
// where dark text already contrasts naturally — a strong light halo blooms
// around dim headers and looks chalky. Keep it to a soft 1px lift. Light theme
// needs the full stack because white text over bright sky is the worst case.
export const SIDEBAR_TEXT_SHADOWS: Record<SidebarTheme, string> = {
  light:
    '0 0 2px rgba(0, 0, 0, 0.9), 0 1px 2px rgba(0, 0, 0, 0.7), 0 0 10px rgba(0, 0, 0, 0.45)',
  dark: '0 1px 2px rgba(255, 255, 255, 0.4)',
};

// drop-shadow() only accepts a single shadow at a time; pairs well with the
// logo's mask-image fill so the glyph lifts off the wallpaper.
export const SIDEBAR_LOGO_SHADOWS: Record<SidebarTheme, string> = {
  light: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))',
  dark: 'drop-shadow(0 1px 2px rgba(255, 255, 255, 0.35))',
};

// Backdrop filter brightness flips to work like a vibrancy material:
// a uniform scrim that darkens bright wallpapers when text is white, and
// lightens dark wallpapers when text is dark. Solves the case where text
// sits over a locally bright region (e.g. sky) that the luminance average
// didn't flag — text-shadow alone isn't enough against near-white skies.
export const SIDEBAR_BACKDROP_FILTERS: Record<SidebarTheme, string> = {
  light: 'blur(24px) saturate(1.5) brightness(0.88)',
  dark: 'blur(24px) saturate(1.5) brightness(1.12)',
};

export function themeToBackdropFilter(theme: SidebarTheme): string {
  return SIDEBAR_BACKDROP_FILTERS[theme];
}

// Widgets sit on the content surface where text is denser and smaller.
// The sidebar's heavier scrim + text-shadow would look crude at widget
// scale — use a softer stack: no brightness adjustment (preserves the
// wallpaper palette behind each card) and a single-layer text shadow.
export const WIDGET_BACKDROP_FILTERS: Record<SidebarTheme, string> = {
  light: 'blur(20px) saturate(1.4)',
  dark: 'blur(20px) saturate(1.4)',
};

export const WIDGET_TEXT_SHADOWS: Record<SidebarTheme, string> = {
  light: '0 1px 2px rgba(0, 0, 0, 0.45)',
  dark: '0 1px 2px rgba(255, 255, 255, 0.3)',
};

export function themeToWidgetBackdropFilter(theme: SidebarTheme): string {
  return WIDGET_BACKDROP_FILTERS[theme];
}

export function themeToWidgetTextShadow(theme: SidebarTheme): string {
  return WIDGET_TEXT_SHADOWS[theme];
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function resolveAutoPreset(pessoaId: string | null | undefined): BgPresetKey {
  const today = new Date().toISOString().slice(0, 10);
  const seed = today + (pessoaId ?? '');
  const idx = hashString(seed) % BG_PRESET_KEYS.length;
  return BG_PRESET_KEYS[idx];
}

export function resolvePresetKey(valor: string, pessoaId: string | null | undefined): BgPresetKey {
  if (valor in BG_PRESETS) {
    return valor as BgPresetKey;
  }
  return resolveAutoPreset(pessoaId);
}

export function resolvePresetUrl(valor: string, pessoaId: string | null | undefined): string {
  return BG_PRESETS[resolvePresetKey(valor, pessoaId)];
}

export function luminanceToTheme(luminance: number): SidebarTheme {
  return luminance < LUMINANCE_THRESHOLD ? 'light' : 'dark';
}

export function themeToFgColor(theme: SidebarTheme): string {
  return SIDEBAR_FG_COLORS[theme];
}

export function themeToTextShadow(theme: SidebarTheme): string {
  return SIDEBAR_TEXT_SHADOWS[theme];
}

export function themeToLogoShadow(theme: SidebarTheme): string {
  return SIDEBAR_LOGO_SHADOWS[theme];
}

export interface PresetThemeResult {
  key: BgPresetKey;
  luminance: number;
  theme: SidebarTheme;
}

export function resolvePresetTheme(
  valor: string,
  pessoaId: string | null | undefined
): PresetThemeResult {
  const key = resolvePresetKey(valor, pessoaId);
  const luminance = PRESET_LUMINANCES[key];
  return { key, luminance, theme: luminanceToTheme(luminance) };
}
